/* eslint-disable */
import React, { useState, useEffect, useRef } from "react";
import { getData } from "utils/Gateway";
import * as url from "helper/UrlHelper";

const GOOGLE_CALENDAR_SCOPE = "https://www.googleapis.com/auth/calendar.events";
const GOOGLE_CALENDAR_API = "https://www.googleapis.com/calendar/v3/calendars/primary/events";
const GIS_SCRIPT_ID = "google-identity-services";

/**
 * GoogleCalendarSync
 *
 * Reusable component that authenticates the user with Google OAuth2 and syncs
 * one or more agenda records to their primary Google Calendar.
 *
 * Props:
 *   agendaIds  {string[]}  Required. Array of agenda IDs to sync.
 *   token      {string}    Required. Backend auth token for fetching agenda details.
 *   className  {string}    Optional. Extra CSS classes for the button.
 *   buttonText {string}    Optional. Custom button label.
 *   onSuccess  {function}  Optional. Called with created event objects on success.
 *   onError    {function}  Optional. Called with error message on failure.
 */
const GoogleCalendarSync = ({
  agendaIds = [],
  token,
  className = "",
  buttonText,
  onSuccess,
  onError,
}) => {
  const [isSyncing, setIsSyncing] = useState(false);
  const [syncStatus, setSyncStatus] = useState(null); // null | "success" | "error"
  const [statusMessage, setStatusMessage] = useState("");
  const tokenClientRef = useRef(null);
  const accessTokenRef = useRef(null);
  const pendingSyncRef = useRef(false);

  // ── Load Google Identity Services script once ──────────────────────────────
  useEffect(() => {
    if (document.getElementById(GIS_SCRIPT_ID)) {
      initTokenClient();
      return;
    }

    const script = document.createElement("script");
    script.id = GIS_SCRIPT_ID;
    script.src = "https://accounts.google.com/gsi/client";
    script.async = true;
    script.defer = true;
    script.onload = initTokenClient;
    document.head.appendChild(script);
  }, []);

  const initTokenClient = () => {
    if (!window.google?.accounts?.oauth2) return;

    const clientId = process.env.REACT_APP_GOOGLE_CLIENT_ID;
    if (!clientId || clientId === "YOUR_GOOGLE_CLIENT_ID_HERE") {
      console.warn("GoogleCalendarSync: REACT_APP_GOOGLE_CLIENT_ID is not configured.");
      return;
    }

    tokenClientRef.current = window.google.accounts.oauth2.initTokenClient({
      client_id: clientId,
      scope: GOOGLE_CALENDAR_SCOPE,
      callback: handleTokenResponse,
    });
  };

  // ── Called by GIS after the user approves / access token is returned ───────
  const handleTokenResponse = async (tokenResponse) => {
    if (tokenResponse.error) {
      setIsSyncing(false);
      setSyncStatus("error");
      setStatusMessage("Google authorization failed: " + tokenResponse.error);
      onError?.("Google authorization failed: " + tokenResponse.error);
      return;
    }

    accessTokenRef.current = tokenResponse.access_token;

    if (pendingSyncRef.current) {
      pendingSyncRef.current = false;
      await performSync(tokenResponse.access_token);
    }
  };

  // ── Fetch agenda details from backend, then create Calendar events ─────────
  const performSync = async (accessToken) => {
    try {
      setIsSyncing(true);
      setSyncStatus(null);
      setStatusMessage("");

      if (!agendaIds || agendaIds.length === 0) {
        throw new Error("No agenda IDs provided.");
      }

      // Fetch each agenda detail in parallel
      const detailRequests = agendaIds.map((id) =>
        getData(
          url.API_BASE_URL +
            url.API_GET_DETAILS_AGENDA +
            `/${id}?token=${token}`
        )
      );

      const detailResponses = await Promise.all(detailRequests);

      const agendas = detailResponses
        .filter((r) => r.status)
        .map((r) => r.data);

      if (agendas.length === 0) {
        throw new Error("Could not fetch agenda details.");
      }

      // Create a Google Calendar event for each agenda
      const eventRequests = agendas.map((agenda) =>
        createCalendarEvent(accessToken, agenda)
      );

      const results = await Promise.allSettled(eventRequests);

      const succeeded = results.filter((r) => r.status === "fulfilled").map((r) => r.value);
      const failed = results.filter((r) => r.status === "rejected");

      if (succeeded.length === agendas.length) {
        setSyncStatus("success");
        setStatusMessage(
          `${succeeded.length} event${succeeded.length > 1 ? "s" : ""} added to Google Calendar.`
        );
        onSuccess?.(succeeded);
      } else if (succeeded.length > 0) {
        setSyncStatus("success");
        setStatusMessage(
          `${succeeded.length} of ${agendas.length} events synced. ${failed.length} failed.`
        );
        onSuccess?.(succeeded);
      } else {
        throw new Error("All events failed to sync.");
      }
    } catch (err) {
      setSyncStatus("error");
      setStatusMessage(err.message || "Sync failed. Please try again.");
      onError?.(err.message);
    } finally {
      setIsSyncing(false);
    }
  };

  // ── Build and POST a single Google Calendar event ─────────────────────────
  const createCalendarEvent = async (accessToken, agenda) => {
    const dateStr = agenda.date; // "YYYY-MM-DD"
    const startDateTime = `${dateStr}T${agenda.starttime}:00`;
    const endDateTime = `${dateStr}T${agenda.endtime}:00`;

    const eventBody = {
      summary: agenda.description || "Available",
      location: agenda.location || "",
      description: agenda.datespecifichours
        ? "Date-specific availability slot"
        : "Regular availability slot",
      start: {
        dateTime: startDateTime,
        timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      },
      end: {
        dateTime: endDateTime,
        timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      },
      reminders: {
        useDefault: true,
      },
    };

    const response = await fetch(GOOGLE_CALENDAR_API, {
      method: "POST",
      headers: {
        Authorization: `Bearer ${accessToken}`,
        "Content-Type": "application/json",
      },
      body: JSON.stringify(eventBody),
    });

    if (!response.ok) {
      const err = await response.json();
      throw new Error(err?.error?.message || "Failed to create event.");
    }

    return response.json();
  };

  // ── Button click: request token (shows consent screen if needed) ───────────
  const handleSync = () => {
    if (!tokenClientRef.current) {
      const clientId = process.env.REACT_APP_GOOGLE_CLIENT_ID;
      if (!clientId || clientId === "YOUR_GOOGLE_CLIENT_ID_HERE") {
        setSyncStatus("error");
        setStatusMessage("Google Client ID is not configured. Contact your administrator.");
        return;
      }
      setSyncStatus("error");
      setStatusMessage("Google Sign-In is still loading. Please try again.");
      return;
    }

    if (!agendaIds || agendaIds.length === 0) {
      setSyncStatus("error");
      setStatusMessage("No agenda selected to sync.");
      return;
    }

    // If we already have a valid access token, skip the consent popup
    if (accessTokenRef.current) {
      performSync(accessTokenRef.current);
      return;
    }

    // Mark that sync should proceed once we get the token
    pendingSyncRef.current = true;
    tokenClientRef.current.requestAccessToken({ prompt: "consent" });
  };

  // ── Dismiss status feedback ────────────────────────────────────────────────
  const dismissStatus = () => {
    setSyncStatus(null);
    setStatusMessage("");
  };

  const label = buttonText || (agendaIds?.length > 1 ? "Sync all to Google Calendar" : "Add to Google Calendar");

  return (
    <div className="google-calendar-sync">
      <button
        type="button"
        className={`btn btn-outline-primary d-flex align-items-center justify-content-center gap-2 py-3 ${className}`}
        onClick={handleSync}
        disabled={isSyncing}
        style={{ borderRadius: "10px", minWidth: "200px" }}
      >
        {isSyncing ? (
          <>
            <span
              className="spinner-border spinner-border-sm"
              role="status"
              aria-hidden="true"
            />
            <span>Syncing…</span>
          </>
        ) : (
          <>
            {/* Google "G" logo inline SVG — no external dependency */}
            <svg
              width="18"
              height="18"
              viewBox="0 0 48 48"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                fill="#EA4335"
                d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"
              />
              <path
                fill="#4285F4"
                d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"
              />
              <path
                fill="#FBBC05"
                d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"
              />
              <path
                fill="#34A853"
                d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"
              />
            </svg>
            <span>{label}</span>
          </>
        )}
      </button>

      {syncStatus && (
        <div
          className={`alert ${
            syncStatus === "success" ? "alert-success" : "alert-danger"
          } border-0 rounded-8 py-2 px-3 d-flex align-items-center gap-2 mt-2`}
          style={{ fontSize: "0.8rem" }}
        >
          <span className="material-symbols-outlined" style={{ fontSize: "1rem" }}>
            {syncStatus === "success" ? "check_circle" : "error"}
          </span>
          <span className="flex-grow-1">{statusMessage}</span>
          <button
            type="button"
            className="btn-close btn-close-sm"
            style={{ fontSize: "0.6rem" }}
            onClick={dismissStatus}
          />
        </div>
      )}
    </div>
  );
};

export default GoogleCalendarSync;
